// Lists of items: plan steps, history rows, settings rows. A white card with rows under hairlines; inside another
// card, just the rows.
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cx } from '@/lib/cx';
import { Icon, type IconName } from './Icon';

export function List({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('list', className)}>{children}</div>;
}

type ListItemProps = {
  title: ReactNode;
  sub?: ReactNode;
  /** Glyph, Tick or Avatar on the left. */
  lead?: ReactNode;
  /** Chip, score, time or chevron on the right. */
  trail?: ReactNode;
  to?: string;
  onClick?: () => void;
  /** done: grayed title · current: the next step */
  state?: 'done' | 'current';
  /** Thin row inside a card, without its own padding. */
  flush?: boolean;
  className?: string;
};

export function ListItem({ title, sub, lead, trail, to, onClick, state, flush, className }: ListItemProps) {
  const cls = cx('item', state, flush && 'flush', className);
  const body = (
    <>
      {lead}
      <span className="grow">
        <span className="title">{title}</span>
        {sub && <span className="sub">{sub}</span>}
      </span>
      {trail !== undefined && <span className="trail">{trail}</span>}
    </>
  );
  if (to) {
    return (
      <Link className={cls} to={to}>
        {body}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button className={cls} type="button" onClick={onClick}>
        {body}
      </button>
    );
  }
  return <div className={cls}>{body}</div>;
}

/** An icon at the start of a row. neutral: gray instead of ink. */
export function Glyph({ icon, neutral, large }: { icon: IconName; neutral?: boolean; large?: boolean }) {
  return (
    <span className={cx('glyph', neutral && 'neutral', large && 'lg')}>
      <Icon name={icon} />
    </span>
  );
}

/** Plan step mark: an ink disc with a check when done, an amber disc for the next step, a ring for one still to come. */
export function Tick({ done, next }: { done?: boolean; next?: boolean }) {
  return (
    <span className={cx('tick', done ? 'done' : next ? 'next' : 'later')}>
      {done ? <Icon name="check" label="Done" /> : next ? <span className="sr-only">Next</span> : null}
    </span>
  );
}

/** Initials in an amber circle: you. */
export function Avatar({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <span className="avatar" aria-hidden="true">
      {initials}
    </span>
  );
}

export const Chevron = () => <Icon name="chevron-right" className="t-3" />;
